import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Modal, Form, Alert, Spinner, Row, Col, Pagination, InputGroup } from 'react-bootstrap';
import { FaEye, FaCheck, FaTimes, FaSearch, FaFilter, FaDownload, FaIdCard, FaUser, FaCalendar, FaArrowLeft, FaHome } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const KycManagement = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({});

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [currentPage, statusFilter, searchQuery]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        status: statusFilter,
        search: searchQuery
      });

      const response = await api.get(`/admin/kyc/documents?${params}`);
      setDocuments(response.data.data.data || []);
      setTotalPages(response.data.data.last_page || 1);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to fetch documents' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/kyc/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching KYC stats:', error);
    }
  };

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setShowViewModal(true);
  };

  const handleApproveDocument = (document) => {
    setSelectedDocument(document);
    setActionType('approve');
    setShowActionModal(true);
  };

  const handleRejectDocument = (document) => {
    setSelectedDocument(document);
    setActionType('reject');
    setShowActionModal(true);
  };

  const handleBulkAction = (action) => {
    if (selectedDocuments.length === 0) {
      setMessage({ type: 'warning', text: 'Please select documents to perform bulk action' });
      return;
    }
    setActionType(action);
    setShowActionModal(true);
  };

  const processAction = async () => {
    setProcessing(true);
    try {
      if (actionType === 'bulk_approve' || actionType === 'bulk_reject') {
        const response = await api.post('/admin/kyc/documents/bulk-update', {
          document_ids: selectedDocuments,
          status: actionType === 'bulk_approve' ? 'approved' : 'rejected',
          rejection_reason: actionType === 'bulk_reject' ? rejectionReason : null
        });

        setMessage({ 
          type: 'success', 
          text: `Bulk ${actionType === 'bulk_approve' ? 'approval' : 'rejection'} completed successfully` 
        });
      } else {
        const response = await api.put(`/admin/kyc/documents/${selectedDocument.id}/status`, {
          status: actionType,
          rejection_reason: actionType === 'reject' ? rejectionReason : null
        });

        setMessage({ 
          type: 'success', 
          text: `Document ${actionType}d successfully` 
        });
      }

      setShowActionModal(false);
      setSelectedDocuments([]);
      setRejectionReason('');
      fetchDocuments();
      fetchStats();

    } catch (error) {
      setMessage({ 
        type: 'danger', 
        text: `Action failed: ${error.response?.data?.message || error.message}` 
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleSelectDocument = (documentId) => {
    setSelectedDocuments(prev => 
      prev.includes(documentId) 
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map(doc => doc.id));
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'danger',
      'under_review': 'info'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const getDocumentTypeIcon = (type) => {
    const icons = {
      'pan': <FaIdCard className="text-primary" />,
      'aadhar': <FaIdCard className="text-success" />,
      'address_proof': <FaUser className="text-info" />,
      'photo': <FaUser className="text-warning" />
    };
    return icons[type] || <FaIdCard />;
  };

  return (
    <div className="container-fluid">
      {/* Breadcrumb Navigation */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/admin/dashboard" className="text-decoration-none">
              <FaHome className="me-1" />
              Dashboard
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <FaIdCard className="me-1" />
            KYC Management
          </li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Link 
            to="/admin/dashboard" 
            className="btn btn-outline-secondary me-3"
            title="Back to Dashboard"
          >
            <FaArrowLeft className="me-1" />
            Back to Dashboard
          </Link>
          <h2 className="mb-0"><FaIdCard className="me-2" />KYC Document Management</h2>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="success" 
            onClick={() => handleBulkAction('bulk_approve')}
            disabled={selectedDocuments.length === 0}
          >
            <FaCheck className="me-1" /> Approve Selected ({selectedDocuments.length})
          </Button>
          <Button 
            variant="danger" 
            onClick={() => handleBulkAction('bulk_reject')}
            disabled={selectedDocuments.length === 0}
          >
            <FaTimes className="me-1" /> Reject Selected ({selectedDocuments.length})
          </Button>
        </div>
      </div>

      {message.text && (
        <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.total_documents || 0}</h3>
              <p className="mb-0">Total Documents</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{stats.pending_documents || 0}</h3>
              <p className="mb-0">Pending Review</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.approved_documents || 0}</h3>
              <p className="mb-0">Approved</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-danger">{stats.rejected_documents || 0}</h3>
              <p className="mb-0">Rejected</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by user name, email, or document number"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="under_review">Under Review</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button variant="outline-primary" onClick={fetchDocuments}>
                <FaFilter className="me-1" /> Filter
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Documents Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading documents...</p>
            </div>
          ) : (
            <>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>
                      <Form.Check
                        type="checkbox"
                        checked={selectedDocuments.length === documents.length && documents.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>User</th>
                    <th>Document Type</th>
                    <th>Document Number</th>
                    <th>File Name</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedDocuments.includes(doc.id)}
                          onChange={() => handleSelectDocument(doc.id)}
                        />
                      </td>
                      <td>
                        <div>
                          <strong>{doc.user_name}</strong>
                          <br />
                          <small className="text-muted">{doc.user_email}</small>
                          <br />
                          <Badge bg="secondary" className="small">{doc.user_role}</Badge>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {getDocumentTypeIcon(doc.document_type)}
                          <span className="ms-2 text-capitalize">{doc.document_type.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td>{doc.document_number || '-'}</td>
                      <td>
                        <small>{doc.file_name}</small>
                        <br />
                        <small className="text-muted">{(doc.file_size / 1024).toFixed(1)} KB</small>
                      </td>
                      <td>{getStatusBadge(doc.status)}</td>
                      <td>
                        <small>{new Date(doc.uploaded_at).toLocaleDateString()}</small>
                        <br />
                        <small className="text-muted">{new Date(doc.uploaded_at).toLocaleTimeString()}</small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleViewDocument(doc)}
                          >
                            <FaEye />
                          </Button>
                          {doc.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={() => handleApproveDocument(doc)}
                              >
                                <FaCheck />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleRejectDocument(doc)}
                              >
                                <FaTimes />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {documents.length === 0 && (
                <div className="text-center py-4">
                  <FaIdCard size={48} className="text-muted mb-3" />
                  <p className="text-muted">No documents found</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={currentPage === i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next 
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* View Document Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Document Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div>
              <Row>
                <Col md={6}>
                  <h6>User Information</h6>
                  <p><strong>Name:</strong> {selectedDocument.user_name}</p>
                  <p><strong>Email:</strong> {selectedDocument.user_email}</p>
                  <p><strong>Role:</strong> {selectedDocument.user_role}</p>
                </Col>
                <Col md={6}>
                  <h6>Document Information</h6>
                  <p><strong>Type:</strong> {selectedDocument.document_type.replace('_', ' ').toUpperCase()}</p>
                  <p><strong>Number:</strong> {selectedDocument.document_number || 'Not provided'}</p>
                  <p><strong>File:</strong> {selectedDocument.file_name}</p>
                  <p><strong>Size:</strong> {(selectedDocument.file_size / 1024).toFixed(1)} KB</p>
                </Col>
              </Row>
              
              <div className="mt-3">
                <h6>Document Preview</h6>
                <div className="text-center">
                  <img 
                    src={selectedDocument.file_url} 
                    alt="Document preview" 
                    className="img-fluid border rounded"
                    style={{ maxHeight: '400px' }}
                  />
                </div>
              </div>

              {selectedDocument.rejection_reason && (
                <Alert variant="danger" className="mt-3">
                  <strong>Rejection Reason:</strong> {selectedDocument.rejection_reason}
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Action Modal */}
      <Modal show={showActionModal} onHide={() => setShowActionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === 'approve' || actionType === 'bulk_approve' ? 'Approve Document(s)' : 'Reject Document(s)'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionType === 'reject' || actionType === 'bulk_reject' ? (
            <Form.Group>
              <Form.Label>Rejection Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                required
              />
            </Form.Group>
          ) : (
            <p>Are you sure you want to approve the selected document(s)?</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>
          <Button 
            variant={actionType.includes('approve') ? 'success' : 'danger'}
            onClick={processAction}
            disabled={processing || (actionType.includes('reject') && !rejectionReason.trim())}
          >
            {processing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              actionType.includes('approve') ? 'Approve' : 'Reject'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default KycManagement;
